/**
 * Regra de estoque mínimo, isolada da tela para ser testável e para ter um lugar só.
 *
 * Três estados por almoxarifado:
 *   - sem configuração  → herda o padrão do produto
 *   - valor maior que 0 → é o mínimo daquele almoxarifado
 *   - zero explícito    → não se cobra mínimo ali (produto que a cidade não trabalha)
 *
 * Zero NÃO é ausência: sem essa distinção, item não trabalhado numa cidade alertaria para sempre,
 * as pessoas se acostumariam a ignorar o vermelho, e o alerta inteiro perderia valor.
 */

export const ID_ALMOX_PADRAO = 0;

const paraNumero = (valor) => {
  if (valor === null || valor === undefined || valor === '') {
    return null;
  }

  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
};

/**
 * Indexa a configuração como `${id_produto}:${id_almox}` para consulta direta.
 */
export const indexarMinimos = (minimos) => {
  const indice = new Map();

  (Array.isArray(minimos) ? minimos : []).forEach((item) => {
    const idProduto = paraNumero(item?.id_produto);
    const idAlmox = paraNumero(item?.id_almox);
    const minimo = paraNumero(item?.minimo);

    if (idProduto === null || idAlmox === null || minimo === null) {
      return;
    }

    indice.set(`${idProduto}:${idAlmox}`, minimo);
  });

  return indice;
};

/**
 * Mínimo que vale para o par, já resolvida a herança. `null` = nenhum mínimo definido.
 */
export const minimoEfetivo = (indice, idProduto, idAlmox) => {
  if (!(indice instanceof Map) || idProduto === null || idProduto === undefined) {
    return null;
  }

  if (idAlmox !== null && idAlmox !== undefined) {
    const excecao = indice.get(`${idProduto}:${idAlmox}`);
    if (excecao !== undefined) {
      return excecao;
    }
  }

  const padrao = indice.get(`${idProduto}:${ID_ALMOX_PADRAO}`);
  return padrao === undefined ? null : padrao;
};

/**
 * Saldo ausente conta como zero: com o SQL corrigido isso é raro, mas quando acontece significa
 * que não há material — que é o alerta mais importante, não motivo para silêncio.
 */
export const abaixoDoMinimo = (indice, idProduto, idAlmox, saldo) => {
  const efetivo = minimoEfetivo(indice, idProduto, idAlmox);

  if (efetivo === null || efetivo <= 0) {
    return false;
  }

  return (paraNumero(saldo) ?? 0) < efetivo;
};

/**
 * Quantos produtos distintos têm alguma configuração — alimenta o indicador de cobertura.
 * Sem ele, ninguém distingue "está tudo em ordem" de "ninguém configurou ainda".
 */
export const contarProdutosConfigurados = (minimos) => new Set(
  (Array.isArray(minimos) ? minimos : [])
    .map((item) => paraNumero(item?.id_produto))
    .filter((id) => id !== null),
).size;

/**
 * Configuração de um produto no formato que o modal edita.
 */
export const configuracaoDoProduto = (minimos, idProduto) => {
  const doProduto = (Array.isArray(minimos) ? minimos : [])
    .filter((item) => paraNumero(item?.id_produto) === paraNumero(idProduto));

  const padrao = doProduto.find((item) => paraNumero(item?.id_almox) === ID_ALMOX_PADRAO);
  const excecoes = {};

  doProduto.forEach((item) => {
    const idAlmox = paraNumero(item?.id_almox);
    if (idAlmox !== null && idAlmox !== ID_ALMOX_PADRAO) {
      excecoes[idAlmox] = paraNumero(item?.minimo);
    }
  });

  return {
    padrao: padrao ? paraNumero(padrao.minimo) : null,
    excecoes,
  };
};
